// src/config/firebase.js
import { initializeApp } from 'firebase/app';
import { getAuth, setPersistence, browserLocalPersistence, inMemoryPersistence } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
// Importamos pero no configuramos aún la capa de compatibilidad
// Se inicializará después de crear la instancia de Firestore
import FirestoreCompatLayer from '../infrastructure/firebase/FirestoreCompatLayer';

// Preferir configuración inyectada por Firebase Hosting (`/__firebase/init.js`) si existe.
// Esto evita hardcodear credenciales y funciona en múltiples entornos.
// https://firebase.google.com/docs/hosting/reference/init-json
let firebaseConfig = undefined;

if (window?.__FIREBASE_CONFIG__) {
  // Hosting inyecta automáticamente este objeto en tiempo de runtime
  firebaseConfig = window.__FIREBASE_CONFIG__;
  console.log('[firebase] Config cargada desde __FIREBASE_CONFIG__');
} else {
  // Fallback a variables de entorno incrustadas en build (CRA sustituye process.env.*)
  firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
  };
}

// Validar configuración de Firebase
const missingKeys = Object.entries(firebaseConfig).filter(([k, v]) => !v);
const isDevelopment = process.env.NODE_ENV === 'development';

if (missingKeys.length > 0) {
  const keys = missingKeys.map(([k]) => k).join(', ');
  
  if (isDevelopment) {
    console.warn(
      `[Firebase] Configuración incompleta en desarrollo: faltan -> [${keys}]. ` +
      'Usando configuración por defecto para desarrollo local.'
    );
    
    // Configuración por defecto para desarrollo
    const defaultConfig = {
      apiKey: 'demo-api-key',
      authDomain: 'nubelytics.firebaseapp.com',
      projectId: 'nubelytics',
      storageBucket: 'nubelytics.appspot.com',
      messagingSenderId: '123456789',
      appId: '1:123456789:web:demo-app-id',
      measurementId: 'G-DEMO'
    };
    
    // Completar configuración con valores por defecto
    Object.keys(defaultConfig).forEach(key => {
      if (!firebaseConfig[key]) {
        firebaseConfig[key] = defaultConfig[key];
      }
    });
  } else {
    throw new Error(
      `Firebase config incompleta: faltan -> [${keys}]. ` +
        'Configura variables de entorno REACT_APP_* o habilita __FIREBASE_CONFIG__ en Hosting.'
    );
  }
}

// Inicializar Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

// Determinar el tipo de persistencia según el contexto
const isEmbedded = window !== window.parent;
const persistenceType = isEmbedded ? inMemoryPersistence : browserLocalPersistence;

// Configurar la persistencia adecuada según si estamos en un iframe o no
setPersistence(auth, persistenceType)
  .then(() => {
    const persistenceMode = isEmbedded ? 'memoria (iframe)' : 'local (navegador)';
    console.log(`Firebase Auth configurado para persistencia en ${persistenceMode}`);
  })
  .catch((error) => {
    console.error('Error configurando persistencia de autenticación:', error);
  });

// Agregar un listener para detectar cambios de autenticación (para debugging)
auth.onAuthStateChanged(user => {
  if (user) {
    console.log('Usuario autenticado detectado:', user.uid);
    // Guardar la marca de tiempo de la última autenticación exitosa para referencia
    sessionStorage.setItem('lastAuthTimestamp', new Date().toISOString());
  } else {
    console.log('Usuario no autenticado o sesión cerrada');
  }
});

// Obtener instancias de los servicios Firebase
let db = getFirestore(app);
const functions = getFunctions(app);

// Aplicar capa de compatibilidad para Firestore
// Esto permite que el código minificado que usa APIs legacy siga funcionando
const firestoreCompat = new FirestoreCompatLayer(db);

// Reemplazamos la instancia original con la versión compatible
// para interceptar llamadas a métodos legacy como db.collection

// Si estamos en desarrollo, conectar a emulador de Firebase Functions
if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_EMULATOR === 'true') {
  console.log('Conectando a emuladores de Firebase...');
  connectFunctionsEmulator(functions, 'localhost', 5001);
}

export { app, auth, db, functions };
